"use client";

import { useEffect, useRef, forwardRef } from "react";
import intlTelInput from "intl-tel-input";
import "intl-tel-input/build/css/intlTelInput.css";
import { cn } from "@/lib/utils";

const IntlTelInput = forwardRef(
  ({ value, onChange, onBlur, className, placeholder, ...props }, ref) => {
    const inputRef = useRef(null);
    const itiRef = useRef(null);

    useEffect(() => {
      if (!inputRef.current) return;

      // Initialize intl-tel-input
      itiRef.current = intlTelInput(inputRef.current, {
        initialCountry: "sa", // Default to Saudi Arabia
        utilsScript:
          "https://cdn.jsdelivr.net/npm/intl-tel-input@25.14.0/build/js/utils.js",
        preferredCountries: ["sa", "ae", "eg", "jo", "kw", "qa", "bh", "us"],
        separateDialCode: true,
        autoPlaceholder: "polite",
      });

      // Set initial value if provided
      if (value) {
        itiRef.current.setNumber(value);
      }

      // Handle input changes
      const handleInput = () => {
        // Get the full international number in E.164 format
        let number = itiRef.current.getNumber();
        const isValid = itiRef.current.isValidNumber();

        // If getNumber returns empty or invalid, try to construct from input
        if (!number || number === "+") {
          const rawValue = inputRef.current.value;
          const countryData = itiRef.current.getSelectedCountryData();
          const dialCode = countryData.dialCode;

          // Extract digits from raw input (excluding the dial code if present)
          const digitsOnly = rawValue.replace(/\D/g, "");

          // Remove dial code digits if they're at the start
          const dialCodeDigits = dialCode.replace(/\D/g, "");
          let phoneDigits = digitsOnly;
          if (digitsOnly.startsWith(dialCodeDigits)) {
            phoneDigits = digitsOnly.slice(dialCodeDigits.length);
          }

          // Construct the full number
          if (phoneDigits.length > 0) {
            number = `+${dialCode}${phoneDigits}`;
          } else {
            number = `+${dialCode}`;
          }
        }

        if (onChange) {
          onChange({
            target: {
              value: number,
              isValid: isValid,
              countryCode: itiRef.current.getSelectedCountryData().iso2,
              dialCode: itiRef.current.getSelectedCountryData().dialCode,
            },
          });
        }
      };

      // Handle country change
      const handleCountryChange = () => {
        handleInput();
      };

      // Handle blur
      const handleBlur = () => {
        if (onBlur) {
          // Get the full international number
          let number = itiRef.current.getNumber();

          // If empty, try to construct from input (same logic as handleInput)
          if (!number || number === "+") {
            const rawValue = inputRef.current.value;
            const countryData = itiRef.current.getSelectedCountryData();
            const dialCode = countryData.dialCode;
            const digitsOnly = rawValue.replace(/\D/g, "");
            const dialCodeDigits = dialCode.replace(/\D/g, "");
            let phoneDigits = digitsOnly;
            if (digitsOnly.startsWith(dialCodeDigits)) {
              phoneDigits = digitsOnly.slice(dialCodeDigits.length);
            }
            if (phoneDigits.length > 0) {
              number = `+${dialCode}${phoneDigits}`;
            } else {
              number = `+${dialCode}`;
            }
          }

          onBlur({
            target: {
              value: number,
              isValid: itiRef.current.isValidNumber(),
            },
          });
        }
      };

      inputRef.current.addEventListener("input", handleInput);
      inputRef.current.addEventListener("blur", handleBlur);
      inputRef.current.addEventListener("countrychange", handleCountryChange);

      // Expose methods via ref
      if (ref) {
        if (typeof ref === "function") {
          ref({
            getNumber: () => itiRef.current?.getNumber(),
            isValidNumber: () => itiRef.current?.isValidNumber(),
            getSelectedCountryData: () =>
              itiRef.current?.getSelectedCountryData(),
            setNumber: (number) => itiRef.current?.setNumber(number),
            input: inputRef.current,
          });
        } else {
          ref.current = {
            getNumber: () => itiRef.current?.getNumber(),
            isValidNumber: () => itiRef.current?.isValidNumber(),
            getSelectedCountryData: () =>
              itiRef.current?.getSelectedCountryData(),
            setNumber: (number) => itiRef.current?.setNumber(number),
            input: inputRef.current,
          };
        }
      }

      return () => {
        if (inputRef.current) {
          inputRef.current.removeEventListener("input", handleInput);
          inputRef.current.removeEventListener("blur", handleBlur);
          inputRef.current.removeEventListener(
            "countrychange",
            handleCountryChange
          );
        }
        if (itiRef.current) {
          itiRef.current.destroy();
        }
      };
    }, []);

    // Update value when prop changes
    useEffect(() => {
      if (itiRef.current && value !== undefined) {
        const currentNumber = itiRef.current.getNumber();
        // If value is empty string or null/undefined, clear the input
        if (!value || value === "") {
          itiRef.current.setNumber("");
          // Also clear the input element directly
          if (inputRef.current) {
            inputRef.current.value = "";
          }
        } else if (currentNumber !== value) {
          itiRef.current.setNumber(value);
        }
      }
    }, [value]);

    return (
      <div className={cn("relative", className)}>
        <input
          ref={inputRef}
          type="tel"
          placeholder={placeholder}
          className={cn(
            "file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-input flex h-9 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
            "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
            "aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive"
          )}
          {...props}
        />
      </div>
    );
  }
);

IntlTelInput.displayName = "IntlTelInput";

export default IntlTelInput;
